'use client';

import React, { useState } from 'react';

interface FormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  message: string;
}

const ContactForm = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('/api/contact-telegram', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', company: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactCards = [
    {
      title: 'Email Us',
      lines: ['admin@shashio.com', 'support@shashio.com'],
      hrefs: ['mailto:admin@shashio.com', 'mailto:support@shashio.com'],
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      title: '24/7 Support',
      lines: ["We're always here to help"],
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      title: 'Live Chat',
      lines: ['Instant support available'],
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      ),
    },
  ];

  const whyChoose = [
    '24-hour turnaround time',
    'Professional quality guaranteed',
    'Unlimited revisions included',
    'Transparent, competitive pricing',
  ];

  const inputClass =
    'w-full px-4 py-3.5 rounded-xl bg-white/[0.03] border border-white/[0.12] text-white placeholder-gray-500 transition-all duration-200 focus:border-purple-500 focus:bg-white/[0.06] focus:outline-none focus:ring-2 focus:ring-purple-500/20';

  const Logo = () => (
    <a href="/homepage" className="flex items-center space-x-2.5 group w-fit">
      <svg className="w-10 h-10 transition-all duration-300 group-hover:scale-110" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M16 1 L27 8 L27 17 L16 24 L5 17 L5 8 Z" fill="url(#cGrad1)" />
        <path d="M16 24 L27 17 L27 24 L16 31 L5 24 L5 17 Z" fill="url(#cGrad2)" />
        <defs>
          <linearGradient id="cGrad1" x1="5" y1="1" x2="27" y2="24" gradientUnits="userSpaceOnUse">
            <stop stopColor="#A78BFA" />
            <stop offset="1" stopColor="#7C3AED" />
          </linearGradient>
          <linearGradient id="cGrad2" x1="5" y1="17" x2="27" y2="31" gradientUnits="userSpaceOnUse">
            <stop stopColor="#7C3AED" />
            <stop offset="1" stopColor="#5B21B6" />
          </linearGradient>
        </defs>
      </svg>
      <span className="text-2xl font-bold tracking-tight text-white">shashio</span>
    </a>
  );

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-[#0B0D17] text-white">
      {/* Background: breathing glows + dot-grid texture */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/3 w-[1100px] h-[1100px] bg-purple-600/10 rounded-full blur-[160px] animate-glow-breathe" />
        <div className="absolute bottom-1/4 right-1/3 w-[850px] h-[850px] bg-blue-600/5 rounded-full blur-[140px] animate-glow-breathe-delayed" />
        <div
          className="absolute inset-0 opacity-[0.025]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
      </div>

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-24">
        <div className="grid lg:grid-cols-[1fr_520px] gap-10 xl:gap-16 items-center">
          {/* ===================== LEFT - INFO ===================== */}
          <div className="hidden lg:block space-y-8">
            <Logo />

            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.12] backdrop-blur-sm text-purple-200 text-xs font-medium">
              <span className="text-sm">💬</span>
              <span>We typically reply within a few hours</span>
            </div>

            <h1 className="text-5xl xl:text-6xl font-bold leading-[1.1] tracking-tight">
              Let&apos;s talk about
              <br />
              <span className="bg-gradient-to-r from-purple-400 via-purple-300 to-blue-400 bg-clip-text text-transparent">
                your project.
              </span>
            </h1>
            <p className="text-lg text-gray-400 leading-relaxed max-w-xl">
              Questions about HDR editing, virtual staging, or bulk orders? Our team is here to help you
              get listing-ready images, fast.
            </p>

            {/* Contact info cards */}
            <div className="space-y-4 max-w-xl">
              {contactCards.map((card, idx) => (
                <div
                  key={idx}
                  className="group flex items-start gap-4 bg-white/[0.03] hover:bg-white/[0.06] backdrop-blur-sm rounded-xl p-4 border border-white/[0.08] hover:border-purple-500/30 transition-all duration-300"
                >
                  <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-purple-500/20 to-blue-500/10 border border-purple-500/20 flex items-center justify-center text-purple-300 group-hover:scale-110 transition-transform">
                    {card.icon}
                  </div>
                  <div>
                    <p className="font-semibold text-white mb-0.5">{card.title}</p>
                    {card.lines.map((line, i) =>
                      card.hrefs ? (
                        <a key={i} href={card.hrefs[i]} className="text-sm block text-gray-400 hover:text-purple-300 transition-colors">
                          {line}
                        </a>
                      ) : (
                        <p key={i} className="text-sm text-gray-400">
                          {line}
                        </p>
                      )
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Why choose */}
            <div className="pt-2">
              <h3 className="text-base font-semibold text-white mb-4">Why choose Shashio?</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {whyChoose.map((item, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <svg className="w-5 h-5 text-purple-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-gray-400">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ===================== RIGHT - FORM CARD ===================== */}
          <div className="relative w-full max-w-[520px] mx-auto">
            {/* Mobile Logo */}
            <div className="lg:hidden flex justify-center mb-8">
              <Logo />
            </div>

            <div className="absolute -inset-4 bg-purple-600/15 rounded-3xl blur-[80px] -z-10" />

            <div className="relative bg-[#13151F]/95 backdrop-blur-2xl rounded-2xl border-2 border-white/[0.12] shadow-[0_30px_80px_rgba(0,0,0,0.6),0_10px_30px_rgba(124,58,237,0.15)] overflow-hidden transition-all duration-500 hover:border-purple-500/30 hover:shadow-[0_35px_90px_rgba(124,58,237,0.4)]">
              <div className="flex items-center justify-between px-4 py-2.5 bg-gradient-to-r from-white/[0.08] to-white/[0.04] border-b border-white/[0.08]">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-500/80" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                  <div className="w-3 h-3 rounded-full bg-green-500/80" />
                </div>
                <div className="text-xs text-gray-400 font-medium">Send us a message</div>
                <div className="w-12" />
              </div>

              <div className="p-8 sm:p-9">
                <div className="mb-7">
                  <h2 className="text-[26px] font-bold leading-tight">
                    Get in{' '}
                    <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                      touch
                    </span>
                  </h2>
                  <p className="text-gray-400 mt-2 text-[15px]">
                    Send us a message and we&apos;ll respond as soon as possible
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium mb-2 text-gray-200">
                      Name <span className="text-red-400">*</span>
                    </label>
                    <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required placeholder="Your full name" className={inputClass} />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium mb-2 text-gray-200">
                        Email <span className="text-red-400">*</span>
                      </label>
                      <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required placeholder="you@example.com" className={inputClass} />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium mb-2 text-gray-200">
                        Phone <span className="text-red-400">*</span>
                      </label>
                      <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} required placeholder="+1 (555) 123-4567" className={inputClass} />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-sm font-medium mb-2 text-gray-200">
                      Company <span className="text-gray-500 font-normal">(Optional)</span>
                    </label>
                    <input type="text" id="company" name="company" value={formData.company} onChange={handleChange} placeholder="Your company name" className={inputClass} />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium mb-2 text-gray-200">
                      Message <span className="text-red-400">*</span>
                    </label>
                    <textarea id="message" name="message" value={formData.message} onChange={handleChange} required rows={5} placeholder="Tell us about your project or inquiry..." className={`${inputClass} resize-none`} />
                  </div>

                  {submitStatus === 'success' && (
                    <div className="px-4 py-3 rounded-xl text-sm flex items-start gap-3 bg-green-500/10 border border-green-500/20 text-green-400">
                      <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span>Thank you! Your message has been sent. We&apos;ll get back to you soon.</span>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="px-4 py-3 rounded-xl text-sm flex items-start gap-3 bg-red-500/10 border border-red-500/20 text-red-400">
                      <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <span>Oops! Something went wrong. Please try again or email us directly.</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="group w-full mt-1 font-bold py-3.5 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white shadow-xl shadow-purple-500/30 hover:shadow-purple-500/50 hover:-translate-y-0.5 hover:scale-[1.01] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:scale-100"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Animations */}
      <style jsx global>{`
        @keyframes glow-breathe {
          0%, 100% { opacity: 0.1; transform: scale(1); }
          50% { opacity: 0.15; transform: scale(1.05); }
        }
        @keyframes glow-breathe-delayed {
          0%, 100% { opacity: 0.05; transform: scale(1); }
          50% { opacity: 0.08; transform: scale(1.03); }
        }
        .animate-glow-breathe { animation: glow-breathe 8s ease-in-out infinite; }
        .animate-glow-breathe-delayed { animation: glow-breathe-delayed 10s ease-in-out infinite; animation-delay: 2s; }
      `}</style>
    </section>
  );
};

export default ContactForm;
